import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GroupsIcon from '@mui/icons-material/Groups';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import { Box, Tooltip, Typography } from '@mui/material';
import { SECURITY_FALLBACKS } from '../../../../../../corelogic/domain/constants/displayFallbacks.constants';
import { FrontendTournament } from '../../../../../../corelogic/domain/models/tournament.model';
import { formatDateShort } from '../../../../../../corelogic/domain/utils/date.utils';
import { isFMMAPublicTournament } from '../../../../../../corelogic/domain/utils/tournament.utils';
import { SafeText } from '../../../../../../corelogic/domain/utils/xssProtection.utils';
import PulsingActiveIcon from '../../Shared/PulsingActiveIcon/PulsingActiveIcon';

const styles = {
  tournamentCardSelected: {
    p: {
      xs: 1,
      sm: 1.5,
    },
    px: {
      xs: 1.5,
      sm: 2,
    },
    borderRadius: 1,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: {
      xs: '64px',
      sm: '68px',
      md: '72px',
      lg: '76px',
      xl: '80px',
    },
    width: '100%',
    transition: 'all 0.2s',
    border: '1px solid rgba(204, 0, 0, 0.6)',
    bgcolor: 'rgba(204, 0, 0, 0.08)',
    position: 'relative',
    cursor: 'pointer',
    '&:hover': {
      bgcolor: 'rgba(204, 0, 0, 0.12)',
      transform: 'translateX(3px)',
      boxShadow: '0 2px 6px rgba(204, 0, 0, 0.25)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: '3px',
      backgroundColor: '#cc0000',
    },
  },
  tournamentCard: {
    p: {
      xs: 1,
      sm: 1.5,
    },
    px: {
      xs: 1.5,
      sm: 2,
    },
    borderRadius: 1,
    bgcolor: 'rgba(40, 40, 40, 0.8)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
    height: {
      xs: '64px',
      sm: '68px',
      md: '72px',
      lg: '76px',
      xl: '80px',
    },
    width: '100%',
    transition: 'all 0.2s',
    cursor: 'pointer',
    border: '1px solid rgba(60, 60, 60, 0.8)',
    '&:hover': {
      bgcolor: 'rgba(60, 60, 60, 0.9)',
      transform: 'translateX(2px)',
      borderColor: 'rgba(204, 0, 0, 0.3)',
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
    },
  },
  tournamentName: {
    fontFamily: 'Bebas Neue',
    fontWeight: 'bold',
    fontSize: {
      xs: '1rem',
      sm: 'clamp(1.2rem, 1.4vw, 1.5rem)',
    },
    color: 'white',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    letterSpacing: '0.05em',
  },
  tournamentInfo: {
    fontFamily: 'Arial',
    fontSize: {
      xs: '0.63rem',
      sm: 'clamp(0.7rem, 0.9vw, 0.8rem)',
    },
    color: '#cccccc',
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    '& svg': {
      fontSize: '1.1rem',
      marginRight: '4px',
    },
  },
  participantSeasonInfo: {
    display: 'flex',
    alignItems: 'center',
    color: '#cccccc',
    fontSize: {
      xs: '0.63rem',
      sm: 'clamp(0.7rem, 0.9vw, 0.8rem)',
    },
    gap: 0.5,
    ml: 1.5,
    '& svg': {
      fontSize: '1.1rem',
      marginRight: '3px',
    },
  },
  dateInfo: {
    display: 'flex',
    alignItems: 'center',
  },
  statusIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.8,
    marginLeft: 'auto',
    paddingLeft: 1,
    '& svg': {
      fontSize: '1.1rem',
    },
  },
  infoContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflow: 'hidden',
  },
  metaContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  iconStyle: {
    fontSize: '1.1rem',
    color: '#cc0000',
    marginRight: 0.2,
  },
  iconContainer: {
    minWidth: '30px',
    display: 'flex',
    justifyContent: 'center',
  },
};

interface TournamentListCardProps {
  tournament: FrontendTournament;
  isSelected: boolean;
  onClick: () => void;
}

const TournamentListCard = ({
  tournament,
  isSelected,
  onClick,
}: TournamentListCardProps) => {
  // Calculate if this is a FMMA public tournament
  const isFMMAPublic = isFMMAPublicTournament(
    tournament.name,
    tournament.creatorName,
    tournament.private,
  );

  return (
    <Box
      sx={isSelected ? styles.tournamentCardSelected : styles.tournamentCard}
      onClick={onClick}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography sx={styles.tournamentName}>
          <SafeText
            text={tournament.name}
            fallback={SECURITY_FALLBACKS.TOURNAMENT_NAME}
          />
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={styles.tournamentInfo}>
            <CalendarMonthIcon sx={{ fontSize: '1.1rem', mr: 0.5 }} />
            {formatDateShort(tournament.startDate)}
            {tournament.endDate
              ? ` - ${formatDateShort(tournament.endDate)}`
              : ' - TBC'}
          </Box>
          <Box sx={styles.participantSeasonInfo}>
            <GroupsIcon />
            {isFMMAPublic ? 'Global' : tournament.participants || 0}
          </Box>
          <Box sx={styles.participantSeasonInfo}>
            <EmojiEventsIcon />
            {/* If seasonIds.length is 0 and it's an FMMA public tournament, show "All Seasons", otherwise show the count */}
            {tournament.seasonIds.length === 0 && isFMMAPublic
              ? 'All Seasons'
              : `${tournament.seasonIds.length} ${tournament.seasonIds.length === 1 ? 'Season' : 'Seasons'}`}
          </Box>
        </Box>
      </Box>

      <Box sx={styles.statusIndicator}>
        <Tooltip
          title={
            tournament.isActive ? 'Active Tournament' : 'Completed Tournament'
          }
        >
          {tournament.isActive ? (
            <PulsingActiveIcon />
          ) : (
            <CheckCircleIcon sx={{ fontSize: '1.1rem', color: '#cccccc' }} />
          )}
        </Tooltip>

        <Tooltip
          title={
            tournament.private ? 'Private Tournament' : 'Public Tournament'
          }
        >
          {tournament.private ? (
            <LockIcon sx={{ fontSize: '1.1rem', color: '#cccccc' }} />
          ) : (
            <PublicIcon sx={{ fontSize: '1.1rem', color: '#cccccc' }} />
          )}
        </Tooltip>
      </Box>
    </Box>
  );
};

export default TournamentListCard;
